import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Questioning } from '@bb-ui/icons/dist/medium';

import { IconButton } from '@bb-ui/react-library';
import { Tooltip } from '@bb-ui/react-library/dist/components/Tooltip';

import { useStyles } from './ActionAreaButtons.styles';
import {
    FeedbackWidget,
    IFeedbackWidgetProps,
} from '../FeedbackWidget/FeedbackWidget';

export interface IActionAreaButtonsAction {
    icon: React.ReactNode;
    title: string;
    action: () => void;
}

export interface IActionAreaButtonsProps {
    action?: IActionAreaButtonsAction;
    helpLink: string;
    vertical?: boolean | undefined;
    /**
     * Adds the feedback widget under the help button, asking users to rate this
     * page's feature. See FeedbackWidget for adding a feature id; the widget
     * hides itself when feedback is off or the user cannot submit.
     */
    feedback?: Omit<IFeedbackWidgetProps, 'launcherClassName' | 'className'>;
}

export const ActionAreaButtons: React.FunctionComponent<
    IActionAreaButtonsProps
> = ({ action, helpLink, vertical = true, feedback }) => {
    const { t } = useTranslation();
    const classes = useStyles();

    const expanded = action?.title !== 'actionAreaButtons.actions.expand.title';

    const getHelpButtonPlacement = () => {
        if (!(vertical || expanded)) {
            return 'top';
        }

        return action ? 'left' : 'top-end';
    };

    const helpButtonPlacement = getHelpButtonPlacement();

    return (
        <aside className={classes.actionAreaButtonsSticky}>
            <div
                className={
                    vertical || expanded
                        ? classes.actionAreaButtonsContainer
                        : classes.actionAreaButtonsContainerHorizontal
                }
            >
                {action && (
                    <Tooltip
                        title={t(action.title)}
                        placement={vertical || expanded ? 'left' : 'top'}
                    >
                        <IconButton
                            className={classes.floatingButton}
                            onClick={action.action}
                            aria-label={t(action.title)}
                        >
                            {action.icon}
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip
                    title={
                        <Trans i18nKey="actionAreaButtons.helpButton.label">
                            <span lang="en">Blackboard Illuminate</span> Help
                        </Trans>
                    }
                    placement={helpButtonPlacement}
                    id="help-button-tooltip"
                >
                    <a
                        aria-label={t('actionAreaButtons.helpButton.ariaLabel')}
                        className={`${classes.floatingButton} ${
                            vertical || expanded
                                ? classes.floatingButtonVertical
                                : classes.floatingButtonHorizontal
                        }`}
                        href={helpLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="help-button"
                    >
                        <Questioning />
                    </a>
                </Tooltip>
                {feedback && (
                    <FeedbackWidget
                        {...feedback}
                        launcherClassName={classes.floatingButton}
                        className={
                            vertical || expanded
                                ? classes.floatingButtonVertical
                                : classes.floatingButtonHorizontal
                        }
                    />
                )}
            </div>
        </aside>
    );
};
